import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Inbox,
  Search,
  Mail,
  Phone,
  Building2,
  Trash2,
  Calendar,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { value: 'converted', label: 'Converted', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'rejected', label: 'Rejected', color: 'bg-gray-100 text-gray-600 border-gray-200' },
];

const Leads = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.put(
        `${API_URL}/api/admin/leads/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/leads/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (error) {
      console.error('Failed to delete lead:', error);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const filtered = items.filter((item) =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.phone?.includes(searchTerm) ||
    item.apartment_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusMeta = (status) =>
    STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

  return (
    <div data-testid="leads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Leads</h1>
          <p className="font-body text-text-secondary">
            {items.length} early-access request{items.length !== 1 ? 's' : ''} from the landing page
          </p>
        </div>
        <Button
          onClick={fetchItems}
          variant="outline"
          className="rounded-xl border-surface-border"
          data-testid="leads-refresh-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, phone or apartment"
          className="pl-10 rounded-xl"
          data-testid="leads-search-input"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border flex flex-col items-center justify-center h-64 text-text-muted">
          <Inbox size={48} className="mb-4 opacity-40" />
          <p className="font-body">
            {searchTerm ? 'No leads match your search' : 'No leads yet'}
          </p>
          {!searchTerm && (
            <p className="font-body text-sm mt-1">Submissions from the landing page will appear here.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead className="bg-background-secondary border-b border-surface-border">
                <tr>
                  <th className="px-4 py-3 text-left font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Name</th>
                  <th className="px-4 py-3 text-left font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Contact</th>
                  <th className="px-4 py-3 text-left font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Apartment</th>
                  <th className="px-4 py-3 text-left font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Submitted</th>
                  <th className="px-4 py-3 text-left font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                  <th className="px-4 py-3 text-right font-body text-xs font-semibold uppercase tracking-wider text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {filtered.map((item) => {
                  const s = statusMeta(item.status);
                  return (
                    <tr key={item.id} className="hover:bg-background-secondary/50 transition-colors" data-testid={`lead-row-${item.id}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                            <span className="font-heading font-bold text-brand-primary text-sm">
                              {item.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="font-body font-semibold text-text-primary">{item.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-sm text-text-primary hover:text-brand-primary transition-colors">
                            <Mail size={14} className="text-text-muted shrink-0" />
                            <span className="truncate max-w-[220px]">{item.email}</span>
                          </a>
                          <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-sm text-text-secondary hover:text-brand-primary transition-colors">
                            <Phone size={14} className="text-text-muted shrink-0" />
                            <span>{item.phone}</span>
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-text-primary">
                          <Building2 size={14} className="text-text-muted shrink-0" />
                          <span className="line-clamp-1">{item.apartment_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-text-secondary whitespace-nowrap">
                          <Calendar size={14} className="text-text-muted shrink-0" />
                          {formatDate(item.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="relative inline-block">
                          <select
                            value={item.status}
                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                            disabled={updatingId === item.id}
                            className={`appearance-none font-body text-xs font-semibold px-3 py-1.5 pr-7 rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:opacity-60 ${s.color}`}
                            data-testid={`lead-status-${item.id}`}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {updatingId === item.id ? (
                            <Loader2 className="absolute right-1.5 top-1/2 -translate-y-1/2 animate-spin pointer-events-none" size={12} />
                          ) : (
                            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" size={12} />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-lg text-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Delete"
                          data-testid={`lead-delete-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
